import { PaymentChannel } from "../model";
import { DatelyObject, Dates, DateTimes } from "../util";
import { InvoicesLogic } from "./InvoicesLogic";

/**
 * Provides business logic for calculating salary date.
 */
export class SalaryDateLogic {

  /**
   * Returns true, if the given date is a valid Salary date for given gross salary payment date.
   *
   * @param salaryDate - Salary date (the date when the salary is in worker).
   * @param iban - Bank account number of the worker.
   * @param paidAt - Date for the gross salary payment.
   * @param isRequestedSalaryDateSet - Boolean indicating if the salary date is the requested salary date.
   * @param channel - Payment channel.
   * @param zeroPayment - A boolean indicating if the net salary payment to worker is zero.
   * @param role - Current client role, only: pro and admin roles have effect currently
   * @returns - True if the given dates are not conflicting (salary date is possible for given paid at date).
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public static isValidSalaryDate(salaryDate: DatelyObject, iban: string | undefined = undefined, paidAt: any = null, isRequestedSalaryDateSet = false, channel: PaymentChannel | undefined = undefined, zeroPayment = false, role: string | undefined = undefined, checkFirstWorkDay = false): boolean {
    const currentDate = Dates.getToday();
    if ( Dates.isSameOrAfter(currentDate, "2025-12-22") &&
         Dates.isBefore(currentDate, "2026-01-01") &&
         Dates.isSameOrAfter(salaryDate, currentDate) &&
         Dates.isBefore(salaryDate, "2026-01-01")) {
      return true;
    }
    if (!Dates.isWorkday(salaryDate)) {
      return false;
    }
    const calculatedSalaryDate = this.calculateSalaryDate(iban, paidAt, isRequestedSalaryDateSet, channel, zeroPayment);
    if (!checkFirstWorkDay) {
      return Dates.getWorkdays(calculatedSalaryDate!, salaryDate!).length  >= 1;
    } else {
      return Dates.getFirstWorkDay(calculatedSalaryDate!, salaryDate!) != undefined;
    }
  }

  /**
   * Calculates the estimated salary date (the date when the salary is in worker) for the given gross salary payment date.
   *
   * @param _iban  - The iban number of the worker.
   * @param paidAt - Date for the salary gross payment.
   * @param isRequestedSalaryDateSet - Flag to indicate, if the requested salary date is set.
   * @param channel - Payment channel.
   * @param zeroPayment - A boolean indicating if the net salary payment to worker is zero.
   */
  public static calculateSalaryDate(_iban: string | undefined = undefined, paidAt: any = null, isRequestedSalaryDateSet = false, channel: PaymentChannel | undefined = undefined, zeroPayment = false): string | undefined {

    let requiredTime = 0;
    let latePaymentDelay = 0;

    let localPaidAtDayDatelyObject = Dates.getDatelyObject(new Date())!;
    let paidAtDayDatelyObject = Dates.getDatelyObject(paidAt) ?? "";
    if (!paidAtDayDatelyObject) {
      // calculate server time from local time by removing offset
      paidAtDayDatelyObject = DateTimes.add(localPaidAtDayDatelyObject,-1 * (DateTimes.getUTCOffset(localPaidAtDayDatelyObject) ?? 0), "minute")!;
    }
    else
    {
      // calculate local time from server time by adding offset
      localPaidAtDayDatelyObject = DateTimes.add(paidAtDayDatelyObject, (DateTimes.getUTCOffset(paidAtDayDatelyObject) ?? 0), "minute")!;
    }

    if (Dates.isWorkday(paidAtDayDatelyObject)) {

      if (channel === PaymentChannel.PalkkausCfaPaytrail ||
          channel === PaymentChannel.PalkkausEcfaPaytrail ) {
        latePaymentDelay = 0;
      }
      else
      {
        latePaymentDelay = ( (DateTimes.getHour(localPaidAtDayDatelyObject) ?? 0) >= 17 || (Dates.getDay(paidAtDayDatelyObject) ?? "") !== (Dates.getDay(localPaidAtDayDatelyObject) ?? "") ) ? this.daysLatePaymentDelay : 0 ;
      }
    }


    const paidAtWorkday = Dates.getWorkday(paidAtDayDatelyObject) ?? "";

    if (InvoicesLogic.isCfa(channel)) {

      requiredTime += this.daysCustomerServiceProcessing;
      if (isRequestedSalaryDateSet) {
          requiredTime += 2;
      } else {
        requiredTime += latePaymentDelay;
        if (channel === PaymentChannel.PalkkausCfaPaytrail ||
          channel === PaymentChannel.PalkkausEcfaPaytrail) {
          requiredTime += 1;
        }
      }
    } else {
      requiredTime += latePaymentDelay + 1;
    }

    const salaryDate = Dates.addWorkdays(paidAtWorkday, (zeroPayment || InvoicesLogic.isPalkkausManualLike(channel) || InvoicesLogic.isPalkkausInvoicesLike(channel) || channel  == PaymentChannel.PalkkausWS || channel == PaymentChannel.PalkkausPSD ) ? 0 : requiredTime);
    return Dates.asDate(salaryDate);
  }
  /**
   * Returns true, if the given salary date is in the past.
   * @param salaryDate - Salary date to check.
   * @param _iban - The iban number of the worker.
   * @param paidAt - Date for the salary gross payment.
   * @param isRequestedSalaryDateSet - Flag to indicate, if the requested salary date is set.
   * @param channel - Payment channel.
   * @param zeroPayment - A boolean indicating if the net salary payment to worker is zero.
   * @returns True if the given salary date is in the past, false otherwise.
   */
  public static isSalaryDateInPast(salaryDate: DatelyObject, _iban: string | undefined = undefined, paidAt: any = null, isRequestedSalaryDateSet = false, channel: PaymentChannel | undefined = undefined, zeroPayment = false): boolean {
    const calculatedSalaryDate = this.calculateSalaryDate(_iban, paidAt, isRequestedSalaryDateSet, channel, zeroPayment);
    return Dates.isBefore(salaryDate, Dates.getDatelyObject(calculatedSalaryDate) ?? "");
  }

  /**
   * Required days to process the incoming payment to worker net payment.
   */
  private static daysCustomerServiceProcessing = 1;

  /**
   * Payment delay if the  payment takes place outside bank hours.
   */
  private static daysLatePaymentDelay = 1;
}
